/**
 * PlanOverview
 *
 * Configuration step for bulk content planning.
 * Allows adjusting volume, platforms, pillar weights, funnel distribution,
 * date range selection, and events/promotions.
 */

import React, { useMemo, useState } from 'react';
import {
    Calendar,
    Users,
    DollarSign,
    Image,
    Video,
    Layers,
    Instagram,
    Facebook,
    Linkedin,
    Twitter,
    CalendarRange,
    ArrowRight,
    Pencil
} from 'lucide-react';
import EventsPanel from './EventsPanel';
import PromotionSelector from './PromotionSelector';

// Platform icons mapping
const PLATFORM_ICONS = {
    instagram: Instagram,
    facebook: Facebook,
    linkedin: Linkedin,
    twitter: Twitter,
    tiktok: () => <span style={{ fontSize: '14px', fontWeight: 600 }}>TT</span>,
    google: () => <span style={{ fontSize: '14px', fontWeight: 600 }}>G</span>
};

const PLATFORMS = [
    { id: 'instagram', name: 'Instagram', organic: true, paid: true },
    { id: 'facebook', name: 'Facebook', organic: true, paid: true },
    { id: 'tiktok', name: 'TikTok', organic: true, paid: true },
    { id: 'linkedin', name: 'LinkedIn', organic: true, paid: true },
    { id: 'twitter', name: 'Twitter/X', organic: true, paid: false },
    { id: 'google', name: 'Google Ads', organic: false, paid: true }
];

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const PlanOverview = ({ plan, pillars, onPlanUpdate, onFunnelDistributionChange, onContentTypeDistributionChange, onContentTypeToggle, onPostCountChange, onTotalChange, selectedEvents = [], onEventsChange, onDateRangeChange, promotions = [], selectedPromotions = [], onPromotionsChange }) => {
    if (!plan) return null;

    const { config, recommendations, startDate, endDate } = plan;
    const { totalPosts, organicPosts, paidCreatives, byFunnelStage, byContentType, byPillar } = recommendations;

    // Calculate days in range
    const daysInRange = useMemo(() => {
        if (!startDate || !endDate) return 30;
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }, [startDate, endDate]);

    // Format date for display
    const formatDateDisplay = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const handleStartDateChange = (e) => {
        const newStartDate = e.target.value;
        // Ensure start date is not after end date
        if (endDate && newStartDate > endDate) {
            onDateRangeChange?.(newStartDate, newStartDate);
        } else {
            onDateRangeChange?.(newStartDate, endDate);
        }
    };

    const handleEndDateChange = (e) => {
        const newEndDate = e.target.value;
        // Ensure end date is not before start date
        if (startDate && newEndDate < startDate) {
            onDateRangeChange?.(startDate, startDate);
        } else {
            onDateRangeChange?.(startDate, newEndDate);
        }
    };

    // Quick presets for date ranges
    const setPreset = (preset) => {
        const today = new Date();
        let start, end;

        switch (preset) {
            case 'thisMonth':
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;
            case 'nextMonth':
                start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
                break;
            case 'next2Weeks':
                start = new Date(today);
                end = new Date(today);
                end.setDate(end.getDate() + 14);
                break;
            case 'next30Days':
                start = new Date(today);
                end = new Date(today);
                end.setDate(end.getDate() + 30);
                break;
            case 'thisQuarter':
                const quarterStart = Math.floor(today.getMonth() / 3) * 3;
                start = new Date(today.getFullYear(), quarterStart, 1);
                end = new Date(today.getFullYear(), quarterStart + 3, 0);
                break;
            default:
                return;
        }

        const formatForInput = (date) => date.toISOString().split('T')[0];
        onDateRangeChange?.(formatForInput(start), formatForInput(end));
    };

    const handlePlatformToggle = (platformId) => {
        const currentPlatforms = config.targetPlatforms || [];
        const newPlatforms = currentPlatforms.includes(platformId)
            ? currentPlatforms.filter(p => p !== platformId)
            : [...currentPlatforms, platformId];

        onPlanUpdate({ targetPlatforms: newPlatforms });
    };

    const handleContentTypeToggleLocal = (type) => {
        // Use the dedicated toggle handler that maintains total post count
        if (onContentTypeToggle) {
            onContentTypeToggle(type);
        } else {
            // Fallback to old behavior
            if (type === 'organic') {
                onPlanUpdate({ includeOrganic: !config.includeOrganic });
            } else {
                onPlanUpdate({ includePaid: !config.includePaid });
            }
        }
    };

    const handleFunnelSliderChange = (stage, value) => {
        const current = { ...byFunnelStage };
        const oldValue = current[stage].percentage;
        const diff = value - oldValue;

        // Adjust other stages proportionally
        const otherStages = ['TOFU', 'MOFU', 'BOFU'].filter(s => s !== stage);
        const totalOther = otherStages.reduce((sum, s) => sum + current[s].percentage, 0);

        const newDistribution = { ...current };
        newDistribution[stage] = { ...current[stage], percentage: value };

        otherStages.forEach(s => {
            const proportion = current[s].percentage / totalOther;
            newDistribution[s] = {
                ...current[s],
                percentage: Math.max(5, Math.round(current[s].percentage - (diff * proportion)))
            };
        });

        // Normalize to ensure total is 100
        const total = Object.values(newDistribution).reduce((sum, v) => sum + v.percentage, 0);
        if (total !== 100) {
            const adjustment = 100 - total;
            const largestStage = otherStages.reduce((a, b) =>
                newDistribution[a].percentage > newDistribution[b].percentage ? a : b
            );
            newDistribution[largestStage].percentage += adjustment;
        }

        onFunnelDistributionChange({
            TOFU: newDistribution.TOFU.percentage,
            MOFU: newDistribution.MOFU.percentage,
            BOFU: newDistribution.BOFU.percentage
        });
    };

    // Get current content type distribution from config or use defaults
    const contentTypeDistribution = config.contentTypeDistribution || { image: 50, video: 30, carousel: 20 };

    const handleContentTypeSliderChange = (type, value) => {
        const current = { ...contentTypeDistribution };
        const oldValue = current[type];
        const diff = value - oldValue;

        // Adjust other types proportionally
        const otherTypes = ['image', 'video', 'carousel'].filter(t => t !== type);
        const totalOther = otherTypes.reduce((sum, t) => sum + current[t], 0);

        const newDistribution = { ...current };
        newDistribution[type] = value;

        otherTypes.forEach(t => {
            const proportion = totalOther > 0 ? current[t] / totalOther : 0.5;
            newDistribution[t] = Math.max(0, Math.round(current[t] - (diff * proportion)));
        });

        // Normalize to ensure total is 100
        const total = Object.values(newDistribution).reduce((sum, v) => sum + v, 0);
        if (total !== 100) {
            const adjustment = 100 - total;
            const largestType = otherTypes.reduce((a, b) =>
                newDistribution[a] > newDistribution[b] ? a : b
            );
            newDistribution[largestType] = Math.max(0, newDistribution[largestType] + adjustment);
        }

        onContentTypeDistributionChange?.(newDistribution);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            {/* Date Range Selector */}
            <div className="card" style={{ padding: 'var(--space-4)' }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-3)'
                }}>
                    <CalendarRange size={20} style={{ color: 'var(--color-primary)' }} />
                    <h4 style={{ margin: 0 }}>Content Period</h4>
                    <span style={{
                        marginLeft: 'auto',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)',
                        background: 'var(--color-bg-secondary)',
                        padding: 'var(--space-1) var(--space-2)',
                        borderRadius: 'var(--radius-sm)'
                    }}>
                        {daysInRange} days
                    </span>
                </div>

                {/* Date Inputs */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    marginBottom: 'var(--space-3)'
                }}>
                    <div style={{ flex: 1 }}>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)',
                            marginBottom: 'var(--space-1)'
                        }}>
                            Start Date
                        </label>
                        <input
                            type="date"
                            value={startDate || ''}
                            onChange={handleStartDateChange}
                            className="input"
                            style={{ width: '100%' }}
                        />
                    </div>
                    <ArrowRight size={20} style={{ color: 'var(--color-text-tertiary)', marginTop: '18px' }} />
                    <div style={{ flex: 1 }}>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)',
                            marginBottom: 'var(--space-1)'
                        }}>
                            End Date
                        </label>
                        <input
                            type="date"
                            value={endDate || ''}
                            onChange={handleEndDateChange}
                            min={startDate || undefined}
                            className="input"
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>

                {/* Quick Presets */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'var(--space-2)'
                }}>
                    <button
                        onClick={() => setPreset('next2Weeks')}
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 'var(--font-size-xs)' }}
                    >
                        Next 2 Weeks
                    </button>
                    <button
                        onClick={() => setPreset('thisMonth')}
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 'var(--font-size-xs)' }}
                    >
                        This Month
                    </button>
                    <button
                        onClick={() => setPreset('nextMonth')}
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 'var(--font-size-xs)' }}
                    >
                        Next Month
                    </button>
                    <button
                        onClick={() => setPreset('next30Days')}
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 'var(--font-size-xs)' }}
                    >
                        Next 30 Days
                    </button>
                    <button
                        onClick={() => setPreset('thisQuarter')}
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 'var(--font-size-xs)' }}
                    >
                        This Quarter
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 'var(--space-4)'
            }}>
                <SummaryCard
                    icon={<Calendar size={24} />}
                    label="Total Posts"
                    value={totalPosts + (selectedEvents?.reduce((sum, e) => sum + (e.briefCount || 1), 0) || 0)}
                    color="var(--color-primary)"
                    editable={true}
                    onChange={onTotalChange}
                />
                <SummaryCard
                    icon={<Users size={24} />}
                    label="Organic"
                    value={organicPosts}
                    color="var(--info-600)"
                    disabled={!config.includeOrganic}
                    editable={config.includeOrganic}
                    onChange={(val) => onPostCountChange?.('organic', val)}
                />
                <SummaryCard
                    icon={<DollarSign size={24} />}
                    label="Paid"
                    value={paidCreatives}
                    color="var(--success-600)"
                    disabled={!config.includePaid}
                    editable={config.includePaid}
                    onChange={(val) => onPostCountChange?.('paid', val)}
                />
            </div>

            {/* Content Type Selection */}
            <div className="card" style={{ padding: 'var(--space-4)' }}>
                <h4 style={{ margin: '0 0 var(--space-3)' }}>Content Types</h4>
                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                    <button
                        onClick={() => handleContentTypeToggleLocal('organic')}
                        className={`btn ${config.includeOrganic ? 'btn-primary' : 'btn-ghost'}`}
                        style={{ flex: 1 }}
                    >
                        <Users size={16} />
                        Organic Content
                    </button>
                    <button
                        onClick={() => handleContentTypeToggleLocal('paid')}
                        className={`btn ${config.includePaid ? 'btn-primary' : 'btn-ghost'}`}
                        style={{ flex: 1 }}
                    >
                        <DollarSign size={16} />
                        Paid Creatives
                    </button>
                </div>
            </div>

            {/* Platform Selection */}
            <div className="card" style={{ padding: 'var(--space-4)' }}>
                <h4 style={{ margin: '0 0 var(--space-3)' }}>Target Platforms</h4>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: 'var(--space-2)'
                }}>
                    {PLATFORMS.map(platform => {
                        const Icon = PLATFORM_ICONS[platform.id];
                        const isSelected = config.targetPlatforms?.includes(platform.id);
                        const isDisabled = (!config.includeOrganic && !platform.paid) ||
                                          (!config.includePaid && !platform.organic);

                        return (
                            <button
                                key={platform.id}
                                onClick={() => handlePlatformToggle(platform.id)}
                                disabled={isDisabled}
                                className={`btn ${isSelected ? 'btn-primary' : 'btn-ghost'}`}
                                style={{
                                    justifyContent: 'flex-start',
                                    opacity: isDisabled ? 0.4 : 1
                                }}
                            >
                                <Icon size={16} />
                                {platform.name}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Funnel Distribution */}
            <div className="card" style={{ padding: 'var(--space-4)' }}>
                <h4 style={{ margin: '0 0 var(--space-4)' }}>Funnel Distribution</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    {['TOFU', 'MOFU', 'BOFU'].map(stage => {
                        const stageData = byFunnelStage[stage];
                        const colors = {
                            TOFU: { bg: 'var(--info-100)', bar: 'var(--info-500)', text: 'var(--info-700)' },
                            MOFU: { bg: 'var(--warning-100)', bar: 'var(--warning-500)', text: 'var(--warning-700)' },
                            BOFU: { bg: 'var(--success-100)', bar: 'var(--success-500)', text: 'var(--success-700)' }
                        };
                        const stageLabels = {
                            TOFU: 'Awareness',
                            MOFU: 'Consideration',
                            BOFU: 'Conversion'
                        };

                        return (
                            <div key={stage}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 'var(--space-2)'
                                }}>
                                    <div>
                                        <span style={{
                                            fontWeight: 600,
                                            color: colors[stage].text
                                        }}>
                                            {stage}
                                        </span>
                                        <span style={{
                                            marginLeft: 'var(--space-2)',
                                            fontSize: 'var(--font-size-sm)',
                                            color: 'var(--color-text-secondary)'
                                        }}>
                                            {stageLabels[stage]}
                                        </span>
                                    </div>
                                    <span style={{ fontWeight: 600 }}>
                                        {stageData.count} posts ({stageData.percentage}%)
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min="5"
                                    max="70"
                                    value={stageData.percentage}
                                    onChange={(e) => handleFunnelSliderChange(stage, parseInt(e.target.value))}
                                    style={{
                                        width: '100%',
                                        accentColor: colors[stage].bar
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Content Format Distribution */}
            <div className="card" style={{ padding: 'var(--space-4)' }}>
                <h4 style={{ margin: '0 0 var(--space-4)' }}>Content Format Distribution</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    {[
                        { type: 'image', label: 'Static Images', icon: <Image size={18} />, color: 'var(--info-500)' },
                        { type: 'video', label: 'Videos', icon: <Video size={18} />, color: 'var(--success-500)' },
                        { type: 'carousel', label: 'Carousels', icon: <Layers size={18} />, color: 'var(--warning-500)' }
                    ].map(({ type, label, icon, color }) => (
                        <div key={type}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 'var(--space-2)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <span style={{ color }}>{icon}</span>
                                    <span style={{ fontWeight: 500 }}>{label}</span>
                                </div>
                                <span style={{ fontWeight: 600 }}>
                                    {byContentType[type]} ({contentTypeDistribution[type]}%)
                                </span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={contentTypeDistribution[type]}
                                onChange={(e) => handleContentTypeSliderChange(type, parseInt(e.target.value))}
                                style={{
                                    width: '100%',
                                    accentColor: color
                                }}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Pillar Distribution */}
            {pillars.length > 0 && (
                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    <h4 style={{ margin: '0 0 var(--space-3)' }}>Pillar Distribution</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {pillars.filter(p => !p.isArchived).map(pillar => {
                            const pillarData = byPillar[pillar.id];
                            if (!pillarData) return null;

                            return (
                                <div
                                    key={pillar.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-3)',
                                        padding: 'var(--space-2) var(--space-3)',
                                        background: 'var(--color-bg-secondary)',
                                        borderRadius: 'var(--radius-md)',
                                        borderLeft: `4px solid ${pillar.color || '#6366F1'}`
                                    }}
                                >
                                    <span style={{ flex: 1, fontWeight: 500 }}>{pillar.name}</span>
                                    <span style={{ color: 'var(--color-text-secondary)' }}>
                                        {pillarData.recommended} posts
                                    </span>
                                    <span style={{
                                        fontSize: 'var(--font-size-sm)',
                                        color: 'var(--color-text-tertiary)'
                                    }}>
                                        ({pillarData.percentage}%)
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Events & Holidays */}
            <EventsPanel
                startDate={startDate}
                endDate={endDate}
                selectedEvents={selectedEvents}
                onEventsChange={onEventsChange}
            />

            {/* Promotions */}
            <PromotionSelector
                startDate={startDate}
                endDate={endDate}
                promotions={promotions}
                selectedPromotions={selectedPromotions}
                onPromotionsChange={onPromotionsChange}
            />
        </div>
    );
};

// Summary Card Component
const SummaryCard = ({ icon, label, value, color, disabled, editable, onChange }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);

    const handleClick = () => {
        if (editable && !disabled) {
            setEditValue(value);
            setIsEditing(true);
        }
    };

    const handleBlur = () => {
        setIsEditing(false);
        if (onChange && editValue !== value) {
            onChange(editValue);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        } else if (e.key === 'Escape') {
            setEditValue(value);
            setIsEditing(false);
        }
    };

    return (
        <div
            onClick={handleClick}
            style={{
                padding: 'var(--space-4)',
                background: disabled ? 'var(--color-bg-tertiary)' : 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'center',
                opacity: disabled ? 0.5 : 1,
                cursor: editable && !disabled ? 'pointer' : 'default',
                position: 'relative',
                transition: 'box-shadow 0.2s',
                ...(editable && !disabled && !isEditing ? {
                    ':hover': { boxShadow: '0 0 0 2px var(--color-primary)' }
                } : {})
            }}
        >
            {/* Edit indicator */}
            {editable && !disabled && !isEditing && (
                <div style={{
                    position: 'absolute',
                    top: 'var(--space-2)',
                    right: 'var(--space-2)',
                    color: 'var(--color-text-tertiary)',
                    opacity: 0.6
                }}>
                    <Pencil size={12} />
                </div>
            )}
            <div style={{ color: disabled ? 'var(--color-text-tertiary)' : color, marginBottom: 'var(--space-2)' }}>
                {icon}
            </div>
            {isEditing ? (
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={editValue}
                    onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        setEditValue(val === '' ? 0 : parseInt(val));
                    }}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    style={{
                        fontSize: 'var(--font-size-2xl)',
                        fontWeight: 700,
                        color: 'var(--color-text-primary)',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: '2px solid var(--color-primary)',
                        outline: 'none',
                        textAlign: 'center',
                        width: '80px'
                    }}
                />
            ) : (
                <div style={{
                    fontSize: 'var(--font-size-2xl)',
                    fontWeight: 700,
                    color: disabled ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)'
                }}>
                    {value}
                </div>
            )}
            <div style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)'
            }}>
                {label}
            </div>
        </div>
    );
};

// Format Card Component
const FormatCard = ({ icon, label, count }) => (
    <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)',
        background: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-md)'
    }}>
        <div style={{ color: 'var(--color-text-secondary)' }}>{icon}</div>
        <div>
            <div style={{ fontWeight: 600 }}>{count}</div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>{label}</div>
        </div>
    </div>
);

export default PlanOverview;
